import { computeSlots } from './mealPlanUtils';

/** Duration bands for CREATE YOUR PLAN Step 2 (replaces 1/2/3 month plans). */

export const SUBSCRIPTION_MIN_DAYS = 3;

export const SUBSCRIPTION_DAY_BANDS = Object.freeze([
  {
    id: 'days_3',
    minDays: 3,
    title: '3+ days',
    note: 'Minimum plan',
    defaultDiscountPercent: 5,
  },
  {
    id: 'days_7',
    minDays: 7,
    title: '7+ days',
    note: 'One week or more',
    defaultDiscountPercent: 5,
  },
  {
    id: 'days_15',
    minDays: 15,
    title: '15+ days',
    note: 'Two weeks or more',
    defaultDiscountPercent: 10,
  },
  {
    id: 'days_30',
    minDays: 30,
    title: '30+ days',
    note: 'A month or more',
    defaultDiscountPercent: 15,
  },
]);

export const DEFAULT_DAY_BAND_ID = 'days_7';

/** Snapshot / API still treat builder plans as custom date ranges. */
export const DAY_BAND_SNAPSHOT_PLAN_ID = 4;

export function getDayBandById(bandId) {
  return SUBSCRIPTION_DAY_BANDS.find((band) => band.id === bandId) || SUBSCRIPTION_DAY_BANDS[0];
}

export function parseIsoDateUtc(value = '') {
  if (!value || !String(value).includes('-')) return null;
  const [year, month, day] = String(value).split('-').map(Number);
  if (!year || !month || !day) return null;
  const date = new Date(Date.UTC(year, month - 1, day));
  return Number.isNaN(date.getTime()) ? null : date;
}

export function toIsoDateUtc(date) {
  if (!date || Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
}

/** Inclusive calendar span: start..end counts both endpoints. */
export function inclusiveCalendarDays(startIso, endIso) {
  const start = parseIsoDateUtc(startIso);
  const end = parseIsoDateUtc(endIso);
  if (!start || !end || end < start) return 0;
  return Math.floor((end.getTime() - start.getTime()) / 86400000) + 1;
}

export function addCalendarDaysIso(startIso, deltaDays) {
  const start = parseIsoDateUtc(startIso);
  if (!start) return '';
  const next = new Date(start.getTime());
  next.setUTCDate(next.getUTCDate() + Number(deltaDays || 0));
  return toIsoDateUtc(next);
}

/** End date for an inclusive N-day window starting on startIso. */
export function endDateForMinDays(startIso, minDays) {
  const days = Math.max(SUBSCRIPTION_MIN_DAYS, Number(minDays) || SUBSCRIPTION_MIN_DAYS);
  return addCalendarDaysIso(startIso, days - 1);
}

/**
 * Count unique calendar dates with at least one scheduled delivery in range.
 * scheduleRows: [{ days: ['Monday', ...] }, ...]
 */
export function countUniqueDeliveryDays(startIso, endIso, scheduleRows = []) {
  if (!startIso || !endIso) return 0;
  const uniqueDates = new Set();
  (scheduleRows || []).forEach((row) => {
    const days = row?.days || [];
    if (!days.length) return;
    computeSlots({ startDate: startIso, endDate: endIso, days }).forEach((slot) => {
      uniqueDates.add(slot.date);
    });
  });
  return uniqueDates.size;
}

/**
 * Smallest inclusive end date on or after startIso with at least minDeliveryDays
 * scheduled delivery days. Falls back to calendar days when no schedule is set.
 */
export function endDateForMinDeliveryDays(startIso, minDeliveryDays, scheduleRows = []) {
  const target = Math.max(SUBSCRIPTION_MIN_DAYS, Number(minDeliveryDays) || SUBSCRIPTION_MIN_DAYS);
  if (!startIso) return '';
  const rows = (scheduleRows || []).filter((row) => (row?.days || []).length > 0);
  if (!rows.length) return endDateForMinDays(startIso, target);

  let end = startIso;
  const maxIterations = 400;
  for (let i = 0; i < maxIterations; i += 1) {
    if (countUniqueDeliveryDays(startIso, end, rows) >= target) return end;
    end = addCalendarDaysIso(end, 1);
  }
  return end;
}

/** Highest band whose minDays the span still qualifies for. */
export function resolveDayBandForDays(dayCount) {
  const days = Number(dayCount) || 0;
  let best = null;
  SUBSCRIPTION_DAY_BANDS.forEach((band) => {
    if (days < band.minDays) return;
    if (!best || band.minDays > best.minDays) best = band;
  });
  return best;
}

/**
 * Merge Admin day-discount rows with the four product bands.
 * Prefer API % when the same minDays exists; ensure 3+ defaults to 5%.
 */
export function mergeVisibleDayBandDiscounts(apiDiscounts = []) {
  const byMin = new Map();
  (apiDiscounts || []).forEach((row) => {
    const minDays = Number(row.minDays);
    const discountPercent = Number(row.discountPercent);
    if (!Number.isFinite(minDays) || minDays < 1) return;
    if (!Number.isFinite(discountPercent) || discountPercent < 0) return;
    const prev = byMin.get(minDays);
    if (!prev || discountPercent > prev.discountPercent) {
      byMin.set(minDays, {
        id: row.id || `api-${minDays}`,
        minDays,
        discountPercent,
        label: row.label || `${minDays}+ days`,
        status: row.status || 'active',
        sortOrder: Number(row.sortOrder) || minDays,
      });
    }
  });

  SUBSCRIPTION_DAY_BANDS.forEach((band, index) => {
    if (!byMin.has(band.minDays)) {
      byMin.set(band.minDays, {
        id: `band-${band.minDays}`,
        minDays: band.minDays,
        discountPercent: band.defaultDiscountPercent,
        label: band.title,
        status: 'active',
        sortOrder: (index + 1) * 10,
      });
    }
  });

  return [...byMin.values()].sort(
    (a, b) => Number(a.minDays) - Number(b.minDays) || Number(a.sortOrder || 0) - Number(b.sortOrder || 0),
  );
}

export function dayBandCardDiscountPercent(band, visibleDiscounts = []) {
  if (!band) return 0;
  const fromVisible = (visibleDiscounts || []).find((row) => Number(row.minDays) === Number(band.minDays));
  if (fromVisible) return Number(fromVisible.discountPercent) || 0;
  return Number(band.defaultDiscountPercent) || 0;
}

/** Customer-facing plan duration value (label column already says "Plan"). */
export function formatSubscriptionPlanLabel(dayCount) {
  const days = Math.max(0, Number(dayCount) || 0);
  if (!days) return '—';
  return `${days} day${days === 1 ? '' : 's'}`;
}
